import { toast } from 'react-toastify';
import { Button } from "@mui/material";
import debounce from 'lodash/debounce';
import { useRecoilValue } from 'recoil';
import TextField from '@mui/material/TextField';
import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { configState } from '../../../../recoil/atoms';
import { IconArrowRight, IconArrowLeft } from "@tabler/icons";
import { PatientApi } from '../../../../api/medical-operations';
import { ErrorMessage, FormikProps, FormikValues, getIn, useFormikContext } from 'formik';
import { BasicInfoPatient } from '../../../../api/medical-operations/models/basic-info-patient';

export default function AutocompletePatient(props: any) {
  const { name } = props;
  const config = useRecoilValue(configState);

  const [inputValue, setInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [resultCount, setResultCount] = useState<number | undefined>(1);
  const [filteredPatients, setFilteredPatients] = useState<BasicInfoPatient[]>([]);

  const formik: FormikProps<FormikValues> = useFormikContext();  

  const getPatients = () => {
  	setIsLoading(true);

    const limit = 20;
    const patient = new PatientApi(config);
    patient
	    .getBasicPatientsInfo(inputValue, currentPage, limit)
      .then((res) => {
        setFilteredPatients(res.data.patients || []);
        setResultCount(res.data.total);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePreviousPage = () => {
    setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
  };

  const handleNextPage = () => {
    if ((currentPage * 20) < (resultCount || 0)) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
    setCurrentPage(1);
  };
  
  useEffect(() => {
    const debouncedGetPatients = debounce(getPatients, 600);
    debouncedGetPatients();
    return () => {
      debouncedGetPatients.cancel();
    };
  }, [inputValue, currentPage]);

  return (
    <>
      <Autocomplete
        autoHighlight
        options={filteredPatients}
        getOptionLabel={(option) =>
          option.FirstName.concat(' ', option.MiddleName, ' ', option.FamilyName)
        }
        sx={{ width: '100%' }}
        onChange={(event, value) => {
          formik.setFieldValue(name, value?.PatientID);
          if (props.setSelectedPatientID) {
            props.setSelectedPatientID(value?.PatientID);
          }
        }}
        loading={!isLoading}
        filterOptions={(options, state) => (options)}
        onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
        isOptionEqualToValue={(option, value) => option?.PatientID === value?.PatientID}
        renderOption={(props, option) => (
          <div key={option.PatientID}>
            <li {...props}>
              <div>
                {option.FirstName} {option.MiddleName} {option.FamilyName}
              </div>
            </li>
            {option === filteredPatients[filteredPatients.length - 1] && 
            resultCount !== undefined && filteredPatients.length < resultCount && (
			      <li key="loadMore" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Button 
                onClick={handlePreviousPage} 
                disabled={isLoading || currentPage === 1} 
                style={{ flex: '1', marginRight: '10px' }}
              >
                <IconArrowLeft style={{ marginRight: 3, width: 40, height: 20}}/> Previous
              </Button>
              <Button 
                onClick={handleNextPage} 
                disabled={isLoading || (currentPage * 20 >= (resultCount || 0))} 
                style={{ flex: '1', marginLeft: '10px' }}
              >
                Next <IconArrowRight style={{ marginLeft: 3, width: 40, height: 20}}/>
              </Button>
            </li>
            )}
          </div>
        )}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label="Choose a patient"
              id={name}
              name={name}
              helperText={<ErrorMessage name={name} />}
              error={!!(getIn(formik.touched, name) && getIn(formik.errors, name))}
              value={inputValue}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(event)}
            />
          );
        }}
      />
    </>
  );
}
